import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import { AiMark } from "@/components/ui/ai-mark";
import { cn } from "@/lib/utils";

/**
 * The seven data-model kinds (`@/components/coach/anser-tip`), narrowed to the
 * four kicker colours a tip can actually render (GAP.md §5): UPPMÄRKSAMMA/
 * risk/leverage share red, MÖJLIGHET is green, ATT GÖRA is amber, everything
 * else (a question to ask, a dialogue starter) is purple. Redeclared locally
 * rather than imported so this stays a self-contained `components/ui`
 * primitive — the literal values match the coach data model's `AnserTipKind`.
 */
export type AnserTipKind =
  | "attention"
  | "risk"
  | "leverage"
  | "opportunity"
  | "todo"
  | "individual"
  | "dialogue";

const KIND_COLOR: Record<AnserTipKind, string> = {
  attention: "text-g-danger",
  risk: "text-g-danger",
  leverage: "text-g-danger",
  opportunity: "text-g-success",
  todo: "text-g-warning",
  individual: "text-g-primary",
  dialogue: "text-g-primary",
};

interface AnserTipProps {
  /** The kicker's kind, and its colour. Omit for a kicker-less advisory note. */
  kind?: AnserTipKind;
  text: string;
  /** AI-generated text carries an AiMark; evergreen starters do not. */
  ai?: boolean;
  /** Where the CTA link goes; omit (or null) when there is none. */
  cta?: { label: string; to: string } | null;
  /** Prefill the composer with this prompt when the text is clicked (still needs `onPick`). */
  prompt?: string | null;
  onPick?: (prompt: string) => void;
  className?: string;
}

/**
 * Anser's tip component (GRA-199, GAP.md §5): one coloured kicker, one
 * sentence, and an optional CTA — merging the two prior implementations
 * (`coach/AnserTipCard.tsx`, a full tip card with a kind kicker, and
 * `Onboarding/AnserTip.tsx`, a kicker-less one-liner in a speech bubble) into
 * a single component. Passing `kind` renders the full card; omitting it
 * renders the simple advisory bubble. Tokens only.
 */
export function AnserTip({ kind, text, ai, cta, prompt, onPick, className }: AnserTipProps) {
  const { t } = useTranslation("nav");

  if (!kind) {
    if (!text) return null;
    return (
      <div
        data-testid="anser-tip"
        className={cn(
          "flex gap-3 rounded-g-lg border border-g-line bg-g-bg-card p-4 shadow-g-card",
          className,
        )}
      >
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-g-pill bg-g-primary-tint text-g-primary"
          aria-hidden="true"
        >
          <Sparkles className="h-4 w-4" />
        </span>
        <p className="text-[13.5px] leading-relaxed text-g-ink-600">{text}</p>
      </div>
    );
  }

  const kicker = t(`anser.kinds.${kind}`);
  const clickable = Boolean(prompt && onPick);

  return (
    <div
      data-testid="anser-tip"
      data-kind={kind}
      className={cn(
        "flex flex-col gap-1.5 rounded-g-lg border border-g-line bg-g-bg-card p-[14px_16px] shadow-g-card",
        className,
      )}
    >
      <div className="flex items-center gap-1.5">
        <span className={cn("text-[11px] font-bold uppercase tracking-[0.06em]", KIND_COLOR[kind])}>
          {kicker}
        </span>
        {ai ? <AiMark className="ml-auto" /> : null}
      </div>
      {clickable ? (
        <button
          type="button"
          onClick={() => onPick?.(prompt ?? "")}
          className="rounded-g-xs text-left text-[13.5px] leading-[1.5] text-g-ink-700 transition-colors duration-150 hover:text-g-ink-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring"
        >
          {text}
        </button>
      ) : (
        <p className="text-[13.5px] leading-[1.5] text-g-ink-700">{text}</p>
      )}
      {cta ? (
        <Link
          to={cta.to}
          className="self-start text-[13px] font-semibold text-g-primary underline-offset-4 hover:text-g-primary-hover hover:underline"
        >
          {cta.label}
          <ArrowRight className="ml-1 inline size-3.5 align-[-2px]" aria-hidden="true" />
        </Link>
      ) : null}
    </div>
  );
}
